import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, ProgressBar, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';
import SpeechInput from '../../components/SpeechInput';

const brandButtonStyle = {
  background: `linear-gradient(180deg, ${colors.redDark} 0%, ${colors.greyDarkest} 100%)`,
  border: 'none',
  color: colors.greyLightest,
  fontWeight: '600',
  padding: '0.75rem 2rem',
  fontSize: '1.1rem',
};

const successButtonStyle = {
  background: `linear-gradient(180deg, ${colors.goldMid} 0%, ${colors.goldDark} 100%)`,
  border: 'none',
  color: colors.greyDarkest,
  fontWeight: '600',
  padding: '0.75rem 2rem',
  fontSize: '1.1rem',
};

const PHASE_NAMES = {
  1: 'The Struggle',
  2: 'The Breakthrough',
  3: 'The Mission'
};

const PHASE_DESCRIPTIONS = {
  1: 'Your personal pain point and what drove you to find a solution',
  2: 'The moment everything changed and why your approach works',
  3: 'Who you serve and why this matters to you'
};

function OriginStory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('intro'); // intro, interview, generating, review, complete
  const [error, setError] = useState('');

  // Interview state
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [questionText, setQuestionText] = useState('');
  const [currentPhase, setCurrentPhase] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(9);
  const [productName, setProductName] = useState('your solution');

  // Answer state
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [followupQuestion, setFollowupQuestion] = useState('');
  const [isFollowup, setIsFollowup] = useState(false);

  // Conversation history for display
  const [conversation, setConversation] = useState([]);

  // Generated story
  const [generatedStory, setGeneratedStory] = useState('');
  const [editedStory, setEditedStory] = useState('');
  const [saving, setSaving] = useState(false);

  // Upload mode
  const [showUpload, setShowUpload] = useState(false);
  const [uploadText, setUploadText] = useState('');
  const [uploading, setUploading] = useState(false);

  const answerInputRef = useRef(null);
  const conversationEndRef = useRef(null);
  const answerBeforeSpeechRef = useRef('');

  useEffect(() => {
    checkStatus();
  }, []);

  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const checkStatus = async () => {
    try {
      const response = await fetch('/api/origin-story/status', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();

        if (data.exists && data.status === 'approved') {
          // Already completed
          setGeneratedStory(data.generated_story);
          setEditedStory(data.edited_story || data.generated_story);
          setStage('complete');
        } else if (data.exists && data.status === 'generated') {
          // Story generated but not approved
          setGeneratedStory(data.generated_story);
          setEditedStory(data.generated_story);
          setStage('review');
        } else if (data.exists && data.status === 'in_progress') {
          // Resume interview
          await startInterview();
          return;
        }
      }
    } catch (err) {
      console.error('Error checking status:', err);
    }
    setLoading(false);
  };

  const startInterview = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/origin-story/start', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentQuestion(data.current_question);
        setQuestionText(data.question_text);
        setCurrentPhase(data.phase);
        setCurrentIndex(data.current_index);
        setTotalQuestions(data.total_questions);
        setProductName(data.product_name);

        // Add first question to conversation
        setConversation([{
          role: 'assistant',
          content: data.question_text
        }]);

        setStage('interview');
      } else {
        const errData = await response.json();
        setError(errData.error || 'Failed to start interview');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
  };

  const handleSubmitAnswer = async () => {
    if (!answer.trim()) {
      setError('Please provide an answer');
      return;
    }

    setSubmitting(true);
    setError('');

    // Add user's answer to conversation
    const newConversation = [...conversation, {
      role: 'user',
      content: answer
    }];
    setConversation(newConversation);

    try {
      const response = await fetch('/api/origin-story/answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question_id: currentQuestion,
          answer: answer.trim(),
          is_followup: isFollowup
        })
      });

      if (response.ok) {
        const data = await response.json();

        if (data.interview_complete) {
          // Move to generating stage
          setConversation([...newConversation, {
            role: 'assistant',
            content: "Excellent! I've got everything I need. Let me write your origin story based on what you shared..."
          }]);
          setStage('generating');
          await generateStory();
        } else if (data.needs_followup) {
          // Show follow-up question
          setFollowupQuestion(data.followup_question);
          setIsFollowup(true);
          setConversation([...newConversation, {
            role: 'assistant',
            content: data.followup_question
          }]);
          setAnswer('');
        } else {
          // Move to next question
          setCurrentQuestion(data.next_question);
          setQuestionText(data.question_text);
          setCurrentPhase(data.phase);
          setCurrentIndex(data.current_index);
          setIsFollowup(false);
          setFollowupQuestion('');

          // Add transition and next question
          const transition = getTransitionMessage(currentPhase, data.phase);
          if (transition) {
            setConversation([...newConversation, {
              role: 'assistant',
              content: transition
            }, {
              role: 'assistant',
              content: data.question_text
            }]);
          } else {
            setConversation([...newConversation, {
              role: 'assistant',
              content: data.question_text
            }]);
          }

          setAnswer('');
        }
      } else {
        const errData = await response.json();
        setError(errData.error || 'Failed to submit answer');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setSubmitting(false);
    setTimeout(() => answerInputRef.current?.focus(), 100);
  };

  const getTransitionMessage = (oldPhase, newPhase) => {
    if (oldPhase !== newPhase) {
      if (newPhase === 2) {
        return "Great, I've got a clear picture of the struggle. Now let's talk about the breakthrough...";
      } else if (newPhase === 3) {
        return "Perfect! Now for the final piece - let's connect your journey to who you serve...";
      }
    }
    return null;
  };

  const generateStory = async () => {
    try {
      const response = await fetch('/api/origin-story/generate', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedStory(data.generated_story);
        setEditedStory(data.generated_story);
        setStage('review');
      } else {
        const errData = await response.json();
        setError(errData.error || 'Failed to generate story');
        setStage('interview');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
      setStage('interview');
    }
  };

  const handleSaveStory = async () => {
    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/origin-story/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ story: editedStory })
      });

      if (response.ok) {
        setStage('complete');
      } else {
        const errData = await response.json();
        setError(errData.error || 'Failed to save story');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setSaving(false);
  };

  const handleStartOver = async () => {
    if (!window.confirm('Start over? Your previous answers will be saved as a draft.')) {
      return;
    }

    setLoading(true);
    try {
      await fetch('/api/origin-story/reset', {
        method: 'POST',
        credentials: 'include'
      });
      setConversation([]);
      setAnswer('');
      setGeneratedStory('');
      setEditedStory('');
      await startInterview();
    } catch (err) {
      setError('Failed to reset. Please try again.');
    }
    setLoading(false);
  };

  const handleUploadStory = async () => {
    if (!uploadText.trim()) {
      setError('Please paste your origin story');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const response = await fetch('/api/origin-story/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ story: uploadText.trim() })
      });

      if (response.ok) {
        setGeneratedStory(uploadText.trim());
        setEditedStory(uploadText.trim());
        setStage('complete');
      } else {
        const errData = await response.json();
        setError(errData.error || 'Failed to upload story');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setUploading(false);
  };

  const getProgress = () => {
    if (stage === 'intro') return 0;
    if (stage === 'interview') return Math.round((currentIndex / totalQuestions) * 80);
    if (stage === 'generating') return 85;
    if (stage === 'review') return 95;
    return 100;
  };

  // ============================================
  // RENDER: Loading
  // ============================================
  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  // ============================================
  // RENDER: Intro
  // ============================================
  if (stage === 'intro') {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <h1 className="text-center mb-4" style={{ color: '#1a365d' }}>
                  Your Origin Story
                </h1>

                <p className="lead text-center mb-4">
                  Every great brand has a story. Let's extract yours.
                </p>

                {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

                <Card className="mb-4" style={{ backgroundColor: '#f8fafc' }}>
                  <Card.Body>
                    <h5 className="mb-3">How This Works</h5>
                    <p className="mb-2">
                      I'll guide you through a short interview (about 10 minutes) to extract your authentic
                      founder story. We'll cover three parts:
                    </p>
                    <ol className="mb-3">
                      <li><strong>The Struggle</strong> - The problem you personally faced</li>
                      <li><strong>The Breakthrough</strong> - How you discovered your solution</li>
                      <li><strong>The Mission</strong> - Who you help and why it matters</li>
                    </ol>
                    <p className="mb-0 text-muted">
                      Your story will be used on your sales page, welcome emails, and other marketing
                      to help customers connect with you authentically.
                    </p>
                  </Card.Body>
                </Card>

                {!showUpload ? (
                  <div className="text-center">
                    <Button
                      size="lg"
                      onClick={startInterview}
                      style={brandButtonStyle}
                      className="me-3"
                    >
                      Start My Origin Story
                    </Button>
                    <Button
                      size="lg"
                      variant="outline-dark"
                      onClick={() => setShowUpload(true)}
                    >
                      I Already Have One
                    </Button>
                  </div>
                ) : (
                  <div>
                    <h5 className="mb-3">Paste Your Origin Story</h5>
                    <Form.Group className="mb-3">
                      <Form.Control
                        as="textarea"
                        rows={10}
                        placeholder="Paste your existing origin story here..."
                        value={uploadText}
                        onChange={(e) => setUploadText(e.target.value)}
                        style={{ fontSize: '1rem', lineHeight: '1.8' }}
                      />
                    </Form.Group>
                    <div className="d-flex justify-content-between">
                      <Button
                        variant="outline-secondary"
                        onClick={() => { setShowUpload(false); setUploadText(''); }}
                      >
                        Back
                      </Button>
                      <Button
                        size="lg"
                        onClick={handleUploadStory}
                        disabled={uploading || !uploadText.trim()}
                        style={successButtonStyle}
                      >
                        {uploading ? (
                          <><Spinner size="sm" className="me-2" />Saving...</>
                        ) : (
                          'Save My Origin Story'
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ============================================
  // RENDER: Interview
  // ============================================
  if (stage === 'interview') {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                {/* Progress */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <Badge style={{ backgroundColor: colors.redDark }} className="me-2">
                        Phase {currentPhase}: {PHASE_NAMES[currentPhase]}
                      </Badge>
                      <small className="text-muted">{PHASE_DESCRIPTIONS[currentPhase]}</small>
                    </div>
                    <span className="text-muted small">{getProgress()}%</span>
                  </div>
                  <ProgressBar
                    now={getProgress()}
                    style={{ height: '8px', backgroundColor: colors.greyLight }}
                  />
                  <p className="mt-2 mb-0 small text-muted">
                    Question {currentIndex} of {totalQuestions}
                  </p>
                </div>

                {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

                {/* Conversation */}
                <div
                  className="mb-4 p-3"
                  style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  {conversation.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`mb-3 p-3 rounded ${
                        msg.role === 'assistant'
                          ? 'bg-white border'
                          : 'text-white ms-4'
                      }`}
                      style={{
                        maxWidth: msg.role === 'user' ? '85%' : '100%',
                        marginLeft: msg.role === 'user' ? 'auto' : '0',
                        backgroundColor: msg.role === 'user' ? colors.redDark : undefined
                      }}
                    >
                      {msg.role === 'assistant' && (
                        <small className="text-muted d-block mb-1">Story Coach</small>
                      )}
                      <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                    </div>
                  ))}
                  <div ref={conversationEndRef} />
                </div>

                {/* Answer Input */}
                <Form.Group className="mb-3">
                  <div className="position-relative">
                    <Form.Control
                      as="textarea"
                      rows={4}
                      placeholder="Share your story... Be specific - concrete details make your story more compelling."
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      ref={answerInputRef}
                      disabled={submitting}
                      style={{ paddingRight: '50px' }}
                    />
                    <SpeechInput
                      onStart={() => {
                        answerBeforeSpeechRef.current = answer;
                      }}
                      onTranscript={(text, isFinal) => {
                        const prefix = answerBeforeSpeechRef.current ? answerBeforeSpeechRef.current + ' ' : '';
                        setAnswer(prefix + text);
                      }}
                      onError={(msg) => setError(msg)}
                      disabled={submitting}
                      className="position-absolute"
                      style={{ right: '8px', bottom: '8px' }}
                      size="sm"
                    />
                  </div>
                  <Form.Text className="text-muted">
                    Tip: The more specific you are, the more authentic your origin story will sound. Click the mic to speak.
                  </Form.Text>
                </Form.Group>

                <Button
                  size="lg"
                  className="w-100"
                  onClick={handleSubmitAnswer}
                  disabled={submitting || !answer.trim()}
                  style={brandButtonStyle}
                >
                  {submitting ? (
                    <><Spinner size="sm" className="me-2" />Processing...</>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ============================================
  // RENDER: Generating
  // ============================================
  if (stage === 'generating') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
            <h3>Writing Your Origin Story...</h3>
            <p className="text-muted">Transforming your journey into a compelling narrative.</p>
          </Col>
        </Row>
      </Container>
    );
  }

  // ============================================
  // RENDER: Review
  // ============================================
  if (stage === 'review') {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <div className="text-center mb-4 p-4" style={{ backgroundColor: '#f0fff4', borderRadius: '8px' }}>
              <h2 style={{ color: '#1a365d' }}>Your Origin Story is Ready!</h2>
              <p className="mb-0">Review and edit if needed, then approve to use across your marketing.</p>
            </div>

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

            <Card className="shadow mb-4">
              <Card.Header style={{ backgroundColor: colors.greyLightest }}>
                <h5 className="mb-0">Your Origin Story</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group>
                  <Form.Control
                    as="textarea"
                    rows={12}
                    value={editedStory}
                    onChange={(e) => setEditedStory(e.target.value)}
                    style={{ fontSize: '1rem', lineHeight: '1.8' }}
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            <div className="d-flex justify-content-between">
              <Button
                variant="outline-secondary"
                onClick={handleStartOver}
              >
                Start Over
              </Button>
              <Button
                size="lg"
                onClick={handleSaveStory}
                disabled={saving || !editedStory.trim()}
                style={successButtonStyle}
              >
                {saving ? (
                  <><Spinner size="sm" className="me-2" />Saving...</>
                ) : (
                  'Approve & Save'
                )}
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // ============================================
  // RENDER: Complete
  // ============================================
  if (stage === 'complete') {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <div className="text-center mb-4 p-4" style={{ backgroundColor: '#f0fff4', borderRadius: '8px' }}>
              <h2 style={{ color: '#2f855a' }}>Origin Story Complete!</h2>
              <p className="mb-0">Your story is now available for use across all your marketing.</p>
            </div>

            <Card className="shadow mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: colors.greyLightest }}>
                <h5 className="mb-0">Your Origin Story</h5>
                <Badge bg="success">Approved</Badge>
              </Card.Header>
              <Card.Body>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: '1rem', lineHeight: '1.8' }}>
                  {editedStory || generatedStory}
                </div>
              </Card.Body>
            </Card>

            <Card className="mb-4" style={{ backgroundColor: '#f8fafc' }}>
              <Card.Body>
                <h5 className="mb-3">Where Your Story Will Be Used</h5>
                <ul className="mb-0">
                  <li><strong>Sales Pages</strong> - "My Story" section to build trust</li>
                  <li><strong>Welcome Emails</strong> - Connecting with new subscribers</li>
                  <li><strong>Social Posts</strong> - Long-form content about your journey</li>
                  <li><strong>About Page</strong> - Your brand story</li>
                </ul>
              </Card.Body>
            </Card>

            <div className="d-flex justify-content-between">
              <Button
                variant="outline-secondary"
                onClick={handleStartOver}
              >
                Create New Version
              </Button>
              <Button
                onClick={() => navigate('/command-center/campaign-hq/sales-page')}
                style={successButtonStyle}
              >
                Go to Sales Page Builder
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return null;
}

export default OriginStory;
